import { dms, LatLonDMS } from './coord-dms';
import { ImportCSVTableFieldModel } from './ImportCSV';
import { ScreeningAngle } from './screening-angle';
import { Sectorization } from './sectorization';

export class VertexCSVContent {
  identifier: string;
  codeId: string;
  codeType: string;
  airspaceName: string;
  lowerVerticalDistanceValue: string;
  upperVerticalDistanceValue: string;
  lowerVerticalDistanceUnits: string;
  upperVerticalDistanceUnits: string;
  latitude: string;
  longitude: string;
  coordpointId: string;

  static map(csvLine: any, table: ImportCSVTableFieldModel[]): VertexCSVContent {

    var line: VertexCSVContent = new VertexCSVContent();
    if (ImportCSVTableFieldModel.getCheckedFromTable("Identifier", table)) {
      line.identifier = csvLine[ImportCSVTableFieldModel.convertLetterToNumber(ImportCSVTableFieldModel.getLetterFromTable("Identifier", table))];
    }
    line.codeId = csvLine[ImportCSVTableFieldModel.convertLetterToNumber(ImportCSVTableFieldModel.getLetterFromTable("Code Identifier", table))];
    line.codeType = csvLine[ImportCSVTableFieldModel.convertLetterToNumber(ImportCSVTableFieldModel.getLetterFromTable("Code Type", table))];
    line.airspaceName = csvLine[ImportCSVTableFieldModel.convertLetterToNumber(ImportCSVTableFieldModel.getLetterFromTable("Airspace Name", table))];
    line.lowerVerticalDistanceValue = csvLine[ImportCSVTableFieldModel.convertLetterToNumber(ImportCSVTableFieldModel.getLetterFromTable("Lower Vertical Distance Value", table))];
    line.upperVerticalDistanceValue = csvLine[ImportCSVTableFieldModel.convertLetterToNumber(ImportCSVTableFieldModel.getLetterFromTable("Upper Vertical Distance Value", table))];
    line.lowerVerticalDistanceUnits = csvLine[ImportCSVTableFieldModel.convertLetterToNumber(ImportCSVTableFieldModel.getLetterFromTable("Lower Vertical Distance Units", table))];
    line.upperVerticalDistanceUnits = csvLine[ImportCSVTableFieldModel.convertLetterToNumber(ImportCSVTableFieldModel.getLetterFromTable("Upper Vertical Distance Units", table))];
    const latString = csvLine[ImportCSVTableFieldModel.convertLetterToNumber(ImportCSVTableFieldModel.getLetterFromTable("Latitude", table))];
    line.latitude = this.convertLatToDegrees(latString);
    line.longitude = this.convertLonToDegrees(csvLine[ImportCSVTableFieldModel.convertLetterToNumber(ImportCSVTableFieldModel.getLetterFromTable("Longitude", table))]);
    return line;
  }

  private static isNumber(string: string) {
    let number: Number = Number(string);
    if (Number.isNaN(number)) {
      return false;
    }
    return true;
  }

  private static convertLatToDegrees(latString: string): string {

    if (!(latString.length > 0)) return null;

    const signal: string = latString.substring(latString.length - 1);

    if (signal != "N" && signal != "S") return "NaN";

    const numberPart = latString.substring(0, latString.length - 1);

    if (!this.isNumber(numberPart)) return "NaN";

    const scanner: string[] = numberPart.split(".");
    let intPart: string = "";
    let decimalPart: string = "";
    let intLatNumber: number;
    let decimalLatNumber: number;


    const latLonDMS: LatLonDMS = new LatLonDMS();

    const dmsRepresentation: dms = { cardinal: null, degrees: null, minutes: null, seconds: null };

    let resultingLat: number;

    //Sets the DMS signal ("N" or "S")
    dmsRepresentation.cardinal = signal;


    let index: number = 0;
    //Gets the integer part of the latitude string and fills the 'dmsRepresentation'
    if (index < scanner.length) {
      intPart = scanner[index];
      dmsRepresentation.degrees = Number.parseInt(intPart.substring(0, 2));

      if (intPart.length > 2) {
        dmsRepresentation.minutes = Number.parseInt(intPart.substring(2, 4));

        if (intPart.length > 4) {
          const secondAux: number = Number.parseInt((intPart.substring(4, 6)));
          dmsRepresentation.seconds = secondAux;
        }
      }
    }
    index++;
    //Gets the decimal part of the latitude string
    if (index < scanner.length) {
      decimalPart = scanner[index];
      decimalLatNumber = Number.parseFloat("0." + decimalPart);

      //If 'latString' is in the form: "DD.dd...X" then it is already in DD format only requires to change signal if  'X' == "S" (south)
      if (intPart.length == 2) {
        intLatNumber = Number.parseInt(intPart);

        resultingLat = intLatNumber + decimalLatNumber;
        if (signal == "S") {
          resultingLat *= -1;
        }

        return resultingLat.toString();
      }
      //If 'latString' is in the form: DDMM.mm...X, then mm represents the seconds, and must be converted to seconds
      else if (intPart.length == 4) {
        const seconds: number = decimalLatNumber * 60;
        dmsRepresentation.seconds = seconds;
      }
      //If 'latString' is in the form: DDMMSS.ss...X, then it is in the DMS format and the seconds are equal to SS + 0.ss
      else if (intPart.length == 6) {
        dmsRepresentation.seconds = dmsRepresentation.seconds + decimalLatNumber;
      }
    }

    //If minutes or seconds were not fill previously then they are equal to 0
    if (dmsRepresentation.minutes == null) {
      dmsRepresentation.minutes = 0;
    }
    if (dmsRepresentation.seconds == null) {
      dmsRepresentation.seconds = 0.0;
    }

    latLonDMS.latitude = dmsRepresentation;

    return latLonDMS.toLat().toString();
  }

  public static convertLonToDegrees(lonString: string): string {

    if (!(lonString.length > 0)) return null;

    const signal: string = lonString.substring(lonString.length - 1);

    if (signal != "W" && signal != "E") return "NaN";

    const numberPart = lonString.substring(0, lonString.length - 1);

    if (!this.isNumber(numberPart)) return "NaN";

    const scanner: string[] = numberPart.split(".");
    let intPart: string = "";
    let decimalPart: string = "";
    let intLonNumber: number;
    let decimalLonNumber: number;

    const latLonDMS: LatLonDMS = new LatLonDMS();

    const dmsRepresentation: dms = { cardinal: null, degrees: null, minutes: null, seconds: null };

    let resultingLon: number;

    //Sets the DMS signal ("W" or "E")
    dmsRepresentation.cardinal = signal;

    let index = 0;
    //Gets the integer part of the longitude string and fills the 'dmsRepresentation'
    if (index < scanner.length) {
      intPart = scanner[index];
      dmsRepresentation.degrees = Number.parseInt(intPart.substring(0, 3));

      if (intPart.length > 3) {
        dmsRepresentation.minutes = Number.parseInt(intPart.substring(3, 5));

        if (intPart.length > 5) {
          const secondAux: number = Number.parseInt((intPart.substring(5, 7)));
          dmsRepresentation.seconds = secondAux;
        }
      }
    }
    index++;
    //Gets the decimal part of the latitude string
    if (index < scanner.length) {
      decimalPart = scanner[index];
      decimalLonNumber = Number.parseFloat("0." + decimalPart);

      //If 'lonString' is in the form: "DDD.dd...X" then it is already in DD format only requires to change signal if  'X' == "W" (west)
      if (intPart.length == 3) {
        intLonNumber = Number.parseInt(intPart);

        resultingLon = intLonNumber + decimalLonNumber;
        if (signal == "W") {
          resultingLon *= -1;
        }

        return resultingLon.toString();
      }
      //If 'latString' is in the form: DDDMM.mm...X, then mm represents the seconds, and must be converted to seconds
      else if (intPart.length == 5) {
        const seconds: number = decimalLonNumber * 60;
        dmsRepresentation.seconds = seconds;
      }
      //If 'latString' is in the form: DDDMMSS.ss...X, then it is in the DMS format and the seconds are equal to SS + 0.ss
      else if (intPart.length == 7) {
        dmsRepresentation.seconds = dmsRepresentation.seconds + decimalLonNumber;
      }
    }

    //If minutes or seconds were not fill previously then they are equal to 0
    if (dmsRepresentation.minutes == null) {
      dmsRepresentation.minutes = 0;
    }
    if (dmsRepresentation.seconds == null) {
      dmsRepresentation.seconds = 0.0;
    }

    latLonDMS.longitude = dmsRepresentation;

    return latLonDMS.toLon().toString();
  }
}

export class VertexCsvModel {
  table: ImportCSVTableFieldModel[] = [];
  dataStartingLine: number = 2;
  fieldSeparator: string = ";";
  decimalSeparator: string = ".";
  fileDataSource: string = "Published";
  file: File;
  fileContent: VertexCSVContent[] = [];

  fillTable(): void {
    this.table.push(new ImportCSVTableFieldModel("Identifier", "A", false));
    this.table.push(new ImportCSVTableFieldModel("Code Identifier", "I", true));
    this.table.push(new ImportCSVTableFieldModel("Code Type", "G", true));
    this.table.push(new ImportCSVTableFieldModel("Airspace Name", "J", true));
    this.table.push(new ImportCSVTableFieldModel("Lower Vertical Distance Value", "K", true));
    this.table.push(new ImportCSVTableFieldModel("Upper Vertical Distance Value", "M", true));
    this.table.push(new ImportCSVTableFieldModel("Lower Vertical Distance Units", "L", true));
    this.table.push(new ImportCSVTableFieldModel("Upper Vertical Distance Units", "N", true));
    this.table.push(new ImportCSVTableFieldModel("Latitude", "AC", true));
    this.table.push(new ImportCSVTableFieldModel("Longitude", "AD", true));
  }
}

export interface CoordinatePoint {
  coordpointid: string,
  id: number,
  uuid?: string;
  latitude: number,
  longitude: number,
  maxaltitude: number,
  minaltitude: number,
  name: string,
  altitude: number,
  nominalaltitude: number,
  notupdate: boolean,
  type: string,
  updated: boolean,
  userdefined: boolean,
  configId: number,
  projectId: number,
  composedName: string;
}

export class CoordinatePointModel implements CoordinatePoint {
  coordpointid: string;
  id: number;
  uuid?: string;
  latitude: number = 0;
  longitude: number = 0;
  latLonDms: LatLonDMS = new LatLonDMS();
  maxaltitude: number = 60000;
  minaltitude: number = 0;
  maxaltitudeMeters: number = 18288.0;
  minaltitudeMeters: number = 0;
  name: string;
  altitude: number;
  nominalaltitude: number;
  nominalaltitudeMeters: number;
  notupdate: boolean = false;
  type: string = 'WAYPOINT';
  updated: boolean = false;
  userdefined: boolean = true;
  isselected: boolean = false;
  configId: number;
  projectId: number;
  published: boolean;
  empty?: boolean;
  composedName: string;
  ismodified: boolean;
  /////// only for coordpoint-status component ///////
  isDuplicate?: boolean;
  invalidId?: boolean;
  ///////////////////////////////////////////////////

  public static readonly LAYER_ID_FEATURE_PROPERTY: string = "layerid";
  public static readonly INDEX_FEATURE_PROPERTY: string = "index";
  public static readonly PUBLISHED_FEATURE_PROPERTY: string = "published";

  static duplicationCheckCondition(coord1: CoordinatePointModel, coord2: CoordinatePointModel) {
    //return coord1.coordpointid == coord2.coordpointid && ((coord1.id == null && coord2.id == null) || coord1.id != coord2.id);
    return coord1.coordpointid == coord2.coordpointid && coord1.id != coord2.id;
  }
  getInformation() {
    let information = "";

    information += '<h4 style="font-weight:bold">Coordinate Point</h4>';
    information += "<p>ID: " + this.coordpointid + "</p>";
    information += "<p>Name: " + this.name + "</p>";
    information += "<p>Type: " + this.type + "</p>";
    information += "<p>Latitude: " + this.latitude + "</p>";
    information += "<p>Longitude: " + this.longitude + "</p>";
    information += "<p>User Defined: " + this.userdefined + "</p>";

    return information;
  }
}
